import { useContext, useEffect } from "react";
import { createGlobalState } from "react-use";
import { ConsumerExperimentsMap, CorpExperimentsMap } from "../types";
import { fetchActiveExperiments } from "../api";
import { ExperimentsHookContext } from "./ExperimentsHookProvider";

interface CombinedExperiments
  extends CorpExperimentsMap,
    ConsumerExperimentsMap {}

type ExperimentsState = {
  experiments: CombinedExperiments;
  trackingProperties: Record<string, string>;
};

const useExperimentsHook = createGlobalState<ExperimentsState | undefined>();
const useActiveExperimentsPromise = createGlobalState<
  Promise<ExperimentsState> | undefined
>(undefined);
const useExperimentsCallState = createGlobalState<"loading" | "resolved">(
  "loading"
);

export const useExperiments = () => {
  const ctx = useContext(ExperimentsHookContext);
  const isLoggedIn = Boolean(ctx?.isLoggedIn);

  const [state, setState] = useExperimentsHook();
  const [promise, setPromise] = useActiveExperimentsPromise();
  const [experimentsCallState, setExperimentsCallState] =
    useExperimentsCallState();

  const experiments = state?.experiments || ({} as CombinedExperiments);

  useEffect(() => {
    // This effect will wait for auth to be set before triggering Experiments API call.
    // And sets the promise singleton so we only fetch once.
    if (isLoggedIn && !promise) {
      setPromise(fetchActiveExperiments());
    }
  }, [isLoggedIn, promise]);

  useEffect(() => {
    // Wait for the fetchActiveExperiments promise to exist, and then resolve it into experiments.
    const resolvePromiseSingleton = async () => {
      try {
        const result = await promise;
        setState(result);
      } finally {
        // Always mark resolved so consumers gating on resolution (e.g. admin
        // routing) fail open to control rather than blocking indefinitely.
        setExperimentsCallState("resolved");
      }
    };

    if (promise) {
      resolvePromiseSingleton();
    }
  }, [promise]);

  return { experiments, experimentsCallState };
};
